import React, { useState, useEffect } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { isPushSupported } from '../../push/push-utils';
import { ensureNotificationPermission } from '../../push/subscribeWebPush';
import { useSecureService } from '../../store/service';
import favicon from '../../images/rev.webp';
import styles from './NotificationPermissionBanner.module.scss';

const DISMISSED_KEY = 'notification_banner_dismissed';

/**
 * Notification permission banner:
 * - permission "default": "Enable Notifications" + Allow / Maybe Later; Allow opens the browser prompt.
 * - permission "denied": "Notifications Blocked" info only (no prompt).
 * - permission "granted": banner hidden.
 * Subscription API is called on each login from AuthChecker (regardless of permission).
 */
const NotificationPermissionBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const { subscribeWebPushIfAllowed } = useSecureService();

  useEffect(() => {
    if (!isPushSupported()) return;

    const permission = Notification.permission;
    if (permission === 'granted') return;

    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    const timer = setTimeout(() => {
      setDenied(permission === 'denied');
      setVisible(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleAllow = async () => {
    if (Notification.permission === 'denied') return;

    setLoading(true);
    try {
      const permission = await ensureNotificationPermission();
      if (permission === 'granted') {
        await subscribeWebPushIfAllowed();
        setVisible(false);
      } else if (permission === 'denied') {
        setDenied(true);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleLater = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.banner}>
        <button
          className={styles.closeButton}
          onClick={handleDismiss}
          aria-label="Close"
        >
          <CloseOutlined />
        </button>

        <div className={styles.bannerContent}>
          <img src={favicon} alt="Revisdom" className={styles.logo} />

          <div className={styles.body}>
            <div className={styles.textContent}>
              {denied ? (
                <>
                  <span className={styles.title}>Notifications Blocked</span>
                  <span className={styles.description}>
                    Click the <strong>lock icon</strong> in the address bar, set Notifications to <strong>"Allow"</strong>, then reload the page.
                  </span>
                </>
              ) : (
                <>
                  <span className={styles.title}>Enable Notifications</span>
                  <span className={styles.description}>
                    Get notified about comments, status changes and updates on your projects.
                  </span>
                </>
              )}
            </div>

            <div className={styles.actions}>
              {!denied && (
                <>
                  <button
                    className={styles.laterButton}
                    onClick={handleLater}
                  >
                    Maybe Later
                  </button>
                  <button
                    className={styles.allowButton}
                    onClick={handleAllow}
                    disabled={loading}
                  >
                    {loading ? 'Enabling...' : 'Allow'}
                  </button>
                </>
              )}
              {denied && (
                <button
                  className={styles.laterButton}
                  onClick={handleDismiss}
                >
                  Got It
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionBanner;
